/****************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-12-10 10:04:43                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2026-04-23 12:20:00                               *
 *****************************************************************************/


/* SUMMARY
  * Imports
  * Styles
  * SearchBar: global search suggestions
*/

/* Imports */
import { Bars3BottomLeftIcon, MagnifyingGlassIcon, WrenchScrewdriverIcon } from "@heroicons/react/24/solid";
import { t } from "../../i18n";
import { api } from "../../services/api/api";
/***/

/* Styles */
import "./SearchBar.scss";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { createTextSearchQuery, SearchOptions, searchWithFilters } from "../../services/api/search";
import { Card, CardBody, Typography } from "@material-tailwind/react";
import Loading from "../loading/Loading";
import { useNavigate } from "react-router-dom";
import ProspectDashCard from "../data/prospect/ProspectDashCard";
import QuotationDashdCard from "../data/quotation/QuotationDashCard";
import WorkorderSearchCard from "../data/workorder/WorkorderSearchCard";
import CustomerDashCard from "../data/customer/CustomerDashCard";
import TechnicianDashCard from "../data/technician/TechnicianDashCard";
import HoseDashCard from "../data/hoses/HoseDashCard";

/***/

const SEARCH_MIN_LENGTH = 4;
const SEARCH_PAGE_SIZE = 3;

function SearchResultCard({
  title,
  subtitle,
  icon,
  onClick
}: {
  title: string;
  subtitle?: string;
  icon: ReactNode;
  onClick?: () => void;
}) {
  return (
    <div className="visit-card cursor-pointer" onClick={() => onClick && onClick()}>
      <div className="flex justify-between items-start gap-3">
        <Typography className="text-blue-600 hover:underline break-all">
          {title}
        </Typography>
        <div className="text-blue-gray-500 shrink-0 mt-0.5">
          {icon}
        </div>
      </div>

      {subtitle && (
        <div className="icon-text-container">
          <Typography className="text-blue-gray-600 break-all">
            {subtitle}
          </Typography>
        </div>
      )}
    </div>
  );
}

export default function SearchBar() {
  const navigate = useNavigate();

  const [search, setSearch] = useState<string>("");
  const [open, setOpen] = useState<boolean>(false);
  const [workorders, setWorkorders] = useState<EasyTech.Workorder[]>();
  const [customers, setCustomers] = useState<EasyTech.Customer[]>();
  const [prospects, setProspects] = useState<EasyTech.Prospect[]>();
  const [quotations, setQuotations] = useState<EasyTech.Quotation[]>();
  const [replenishments, setReplenishments] = useState<EasyTech.Replenishment[]>();
  const [parts, setParts] = useState<EasyTech.Part[]>();
  const [technicians, setTechnicians] = useState<EasyTech.Technician[]>();
  const [hoses, setHoses] = useState<EasyTech.Hose[]>();

  const hasSearchValue = search.trim().length >= SEARCH_MIN_LENGTH;

  const resetResults = useCallback(() => {
    setWorkorders(undefined);
    setCustomers(undefined);
    setProspects(undefined);
    setQuotations(undefined);
    setReplenishments(undefined);
    setParts(undefined);
    setTechnicians(undefined);
    setHoses(undefined);
  }, []);

  const searchAll = useCallback(async () => {
    if (!hasSearchValue) {
      setOpen(false);
      resetResults();
      return;
    }

    setOpen(true);
    const searchOptions: SearchOptions = {
      paginate: { pageSize: SEARCH_PAGE_SIZE, pageNumber: 0 },
      queries: []
    };

    searchWithFilters<EasyTech.Workorder>(
      "workorders",
      { ...searchOptions, queries: [createTextSearchQuery(["ref"], search)] }
    ).then((ret) => {
      setWorkorders(ret.data as EasyTech.Workorder[]);
    });

    searchWithFilters<EasyTech.Customer>(
      "customers",
      { ...searchOptions, queries: [createTextSearchQuery(["name", "nip"], search)] }
    ).then((ret) => {
      setCustomers(ret.data as EasyTech.Customer[]);
    });

    searchWithFilters<EasyTech.Prospect>(
      "prospects",
      { ...searchOptions, queries: [createTextSearchQuery(["ref"], search)] }
    ).then((ret) => {
      setProspects(ret.data as EasyTech.Prospect[]);
    });

    searchWithFilters<EasyTech.Quotation>(
      "quotations",
      { ...searchOptions, queries: [createTextSearchQuery(["ref"], search)] }
    ).then((ret) => {
      setQuotations(ret.data as EasyTech.Quotation[]);
    });

    searchWithFilters<EasyTech.Replenishment>(
      "replenishments",
      { ...searchOptions, queries: [createTextSearchQuery(["ref"], search)] }
    ).then((ret) => {
      setReplenishments(ret.data as EasyTech.Replenishment[]);
    });

    searchWithFilters<EasyTech.Part>(
      "parts",
      { ...searchOptions, queries: [createTextSearchQuery(["label", "ref"], search)] }
    ).then((ret) => {
      setParts(ret.data as EasyTech.Part[]);
    });

    api.get("admin/technicians/search?name=" + search)
      .then((ret) => {
        setTechnicians(ret as EasyTech.Technician[]);
      });

    searchWithFilters<EasyTech.Hose>(
      "hoses",
      { ...searchOptions, queries: [createTextSearchQuery(["name", "qrCode", "barCode"], search)] }
    ).then((ret) => {
      setHoses(ret.data as EasyTech.Hose[]);
    });
  }, [hasSearchValue, resetResults, search]);

  useEffect(() => {
    searchAll();
  }, [searchAll]);

  const isLoading = hasSearchValue && [
    workorders,
    customers,
    prospects,
    quotations,
    replenishments,
    parts,
    technicians,
    hoses
  ].some((results) => results === undefined);

  const hasResults = [
    workorders,
    customers,
    prospects,
    quotations,
    replenishments,
    parts,
    technicians,
    hoses
  ].some((results) => (results?.length || 0) > 0);

  return (
    <>
      <div className="z-1 search-input relative">
        <input type="text" placeholder={t("common:search")} onChange={(e) => setSearch(e.target.value)} />
        <MagnifyingGlassIcon className="search-icon" />

        {open && (
          <Card
            className="z-10 shadow-none border rounded-lg border-gray-300 absolute w-[60vw] max-h-[80vh] overflow-y-auto"
            onClick={() => setOpen(false)}
          >
            <CardBody className="flex flex-col">
              <div>
                {workorders ? (
                  workorders.length > 0 &&
                    <div className="mt-3">
                      <Typography variant="h5">
                        {t("common:workorders")}
                      </Typography>
                      {workorders.map((el) => (
                        <WorkorderSearchCard key={el.ref} onClick={() => navigate(`/workorders/${el.id}`)} workorder={el} />
                      ))}
                    </div>
                ) : <Loading />}

                {customers ? (
                  customers.length > 0 &&
                    <div className="mt-3">
                      <Typography variant="h5">
                        {t("common:customers")}
                      </Typography>
                      {customers.map((el) => (
                        <CustomerDashCard key={el.ref} onClick={() => navigate(`/customers/${el.id}`)} customer={el} />
                      ))}
                    </div>
                ) : <Loading />}

                {prospects ? (
                  prospects.length > 0 &&
                    <div className="mt-3">
                      <Typography variant="h5">
                        {t("common:visits")}
                      </Typography>
                      {prospects.map((el) => (
                        <ProspectDashCard key={el.id} onClick={() => navigate(`/visits/${el.id}`)} visit={el} />
                      ))}
                    </div>
                ) : <Loading />}

                {quotations ? (
                  quotations.length > 0 &&
                    <div className="mt-3">
                      <Typography variant="h5">
                        {t("common:quotations")}
                      </Typography>
                      {quotations.map((el, i) => (
                        <QuotationDashdCard
                          key={`quotation-${el.id}-${el.ref || i}`}
                          quotation={el}
                          onClick={() => navigate(`/quotations/${el.id}`)}
                        />
                      ))}
                    </div>
                ) : <Loading />}

                {replenishments ? (
                  replenishments.length > 0 &&
                    <div className="mt-3">
                      <Typography variant="h5">
                        {t("replenishment:title")}
                      </Typography>
                      {replenishments.map((el) => (
                        <SearchResultCard
                          key={`replenishment-${el.id}`}
                          title={el.ref || `#${el.id.slice(0, 8)}`}
                          subtitle={el.technician ? `${el.technician.firstName} ${el.technician.lastName}`.trim() : undefined}
                          icon={<Bars3BottomLeftIcon className="w-5" />}
                          onClick={() => navigate(`/replenishments/${el.id}`)}
                        />
                      ))}
                    </div>
                ) : <Loading />}

                {parts ? (
                  parts.length > 0 &&
                    <div className="mt-3">
                      <Typography variant="h5">
                        {t("common:parts")}
                      </Typography>
                      {parts.map((el) => (
                        <SearchResultCard
                          key={`part-${el.id}`}
                          title={el.label}
                          subtitle={el.ref || undefined}
                          icon={<WrenchScrewdriverIcon className="w-5" />}
                          onClick={() => navigate(`/parts/${el.id}`)}
                        />
                      ))}
                    </div>
                ) : <Loading />}

                {technicians ? (
                  technicians.length > 0 &&
                    <div className="mt-3">
                      <Typography variant="h5">
                        {t("menu:technicians")}
                      </Typography>
                      {technicians.map((el, index) => (
                        <TechnicianDashCard key={`tech-${index}`} onClick={() => navigate(`/users/${el.id}?userType=TECHNICIAN`)} technician={el} />
                      ))}
                    </div>
                ) : <Loading />}

                {hoses ? (
                  hoses.length > 0 &&
                    <div className="mt-3">
                      <Typography variant="h5">
                        {t("workorder:tabs.hoses")}
                      </Typography>
                      {hoses.map((el, index) => (
                        <HoseDashCard key={`hose-${index}`} onClick={() => navigate(`/workorders/${el.workOrder?.id}?tab=${el.machine ? "machines" : "hoses"}`)} hose={el} />
                      ))}
                    </div>
                ) : <Loading />}

                {!isLoading && !hasResults && (
                  <Typography className="mt-3 text-blue-gray-500">
                    {t("replenishment:messages.noData")}
                  </Typography>
                )}
              </div>
            </CardBody>
          </Card>
        )}
      </div>

      {open && (
        <div className="absolute top-0 left-[-80px] w-[100vw] h-[100vh]" onClick={() => setOpen(false)}></div>
      )}
    </>
  );
}
/***/
